import React, { useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid } from "@material-ui/core";
import { useForm } from "react-hook-form";
import SimpleSelect from "../../components/common/SimpleSelect";
import TextFieldOutlined from "../../components/common/TextFieldOutlined";
import { useStore } from "../../hooks/useStore";
import { useTranslation } from "../../hooks/useTranslation";
import { countryList } from "../../utils/countryCodes";
import { manualInputSchema } from "../../utils/validations/ManualInputValidationSchema";
import { EntrantTabIndex } from "../../utils/tabIndex";

type ManualSearchProps = {
  onSubmit?: (dni: string, countryCode: string) => void;
  onCancel?: () => void;
};

export const checkDni = (dni: string) => {
  // 1. Ensure there are more than 6 numbers and less than 10 numbers.
  // 2. Ensure there are no two letters consecutively.
  // 3. Ensure there are no special characters except for spaces.
  const regex = /^(?=(?:\D*\d){7,12}\D*$)(?![a-zA-Z]{2})[a-zA-Z0-9 ]*$/;
  return regex.test(dni);
};

const ManualSearchForm = ({ onSubmit, onCancel }: ManualSearchProps) => {
  const { t, BundleKey } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(manualInputSchema(t)),
  });
  const dniInput = useRef<HTMLInputElement>();
  const { authStore, configStore } = useStore();
  const [dni, setDni] = useState("");
  const [countryCode, setCountryCode] = useState(authStore.country?.countryCode || "ARG");

  const dniRegistration = register("dni");

  const onSubmitUser = () => {
    if (onSubmit) onSubmit(dni, countryCode);
  };

  const onNoSubmit = (e: any) => e.preventDefault();

  const handleClear = () => {
    setDni("");
    dniInput?.current?.focus();
    if (onCancel) onCancel();
  };

  const errorMsg = () => {
    return dni.length > 6 && !checkDni(dni) ? `${t("dni_input_error")}` : "";
  };

  return (
    <form noValidate onSubmit={!configStore.isScanning ? handleSubmit(onSubmitUser) : onNoSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextFieldOutlined
            name="dni"
            type="text"
            required
            label={t(BundleKey.MANUALINPUT_DNI)}
            value={dni}
            onChange={setDni}
            registration={dniRegistration}
            inputRef={dniInput}
            InputProps={{
              tabIndex: EntrantTabIndex.dni,
            }}
            autoFocus
            errors={errors.dni?.message || errorMsg()}
          />
        </Grid>
        <Grid item xs={12}>
          <SimpleSelect
            id="codeCountry"
            label={t(BundleKey.MANUALINPUT_COUNTRY)}
            required
            value={countryCode}
            onChange={(value) => setCountryCode(value)}
            items={countryList}
            inputProps={{
              tabIndex: EntrantTabIndex.country,
              type: "text",
            }}
            errors={errors.countryCode?.message}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container justifyContent="space-between">
            <Grid item xs={5}>
              <Button
                onClick={handleClear}
                color="primary"
                variant="contained"
                disabled={checkDni(dni) || !dni.length}
                tabIndex={EntrantTabIndex.cancel}
                fullWidth
              >
                {t(BundleKey.ACTION_EMPTY)}
              </Button>
            </Grid>
            <Grid item xs={5}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={!checkDni(dni)}
                tabIndex={EntrantTabIndex.accept}
                fullWidth
              >
                {t(BundleKey.ACTION_ACCEPT)}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default ManualSearchForm;
